<script setup lang="ts">
  import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
  import { useModal } from '@/composition/use-modal';
  import { useUi } from '@/composition/use-ui';
  import { AlertType } from '@/interfaces/IAlert';
  import { copyTextToClipboard } from '@/utils/clipboard.utils';
  import { PosAdminGrantsService } from '@/api/services/pos-admin-grants.service';

  const ui = useUi();
  const modals = useModal();

  // State
  const isLoading = ref(true);
  const hasError = ref(false);
  const errorText = ref<string | null>(null);
  const grantId = ref<string | null>(null);
  const code = ref<string | null>(null);
  const expiresAtMs = ref<number | null>(null);
  const openedAtMs = ref(Date.now());
  const nowMs = ref(Date.now());

  let ticker: number | null = null;

  onMounted(async () => {
    await generateToken();

    ticker = window.setInterval(() => {
      nowMs.value = Date.now();
    }, 250);
  });

  onBeforeUnmount(() => {
    if (ticker != null) {
      clearInterval(ticker);
      ticker = null;
    }
  });

  function getFriendlyErrorMessage(error: any): string | null {
    const errorData = error?.response?._data || error?.response?.data;
    const msg = errorData?.message;

    if (typeof msg !== 'string' || msg.trim().length === 0) {
      return null;
    }

    switch (msg) {
      case 'FORBIDDEN:INSUFFICIENT_PERMISSION':
        return 'Su usuario no tiene permisos para generar tokens POS.';
      case 'POS_ADMIN_GRANTS_SECRET is not configured':
        return 'El servidor no está configurado para generar tokens POS. Configure POS_ADMIN_GRANTS_SECRET.';
      default:
        return null;
    }
  }

  async function generateToken() {
    isLoading.value = true;
    hasError.value = false;
    errorText.value = null;

    try {
      const res = await PosAdminGrantsService.generate();
      grantId.value = res.grantId;
      code.value = res.code;
      expiresAtMs.value = res.expiresAtMs;
      openedAtMs.value = Date.now();
    } catch (error) {
      hasError.value = true;
      const friendlyMessage = getFriendlyErrorMessage(error);
      if (friendlyMessage) {
        errorText.value = friendlyMessage;
        ui.showToast(AlertType.Error, friendlyMessage);
      } else {
        errorText.value = 'No se pudo generar el token';
        ui.serverError(error, 'Error al generar el token POS');
      }
    } finally {
      isLoading.value = false;
    }
  }

  const totalMs = computed(() => {
    if (!expiresAtMs.value) return 1;
    return Math.max(1, expiresAtMs.value - openedAtMs.value);
  });

  const remainingMs = computed(() => {
    if (!expiresAtMs.value) return 0;
    return Math.max(0, expiresAtMs.value - nowMs.value);
  });

  const isExpired = computed(() => remainingMs.value <= 0);

  const progress = computed(() => {
    return Math.max(
      0,
      Math.min(100, (remainingMs.value / totalMs.value) * 100)
    );
  });

  const progressColor = computed(() => {
    if (progress.value <= 15) return 'error';
    if (progress.value <= 40) return 'warning';
    return 'success';
  });

  const formattedRemaining = computed(() => {
    const totalSeconds = Math.ceil(remainingMs.value / 1000);
    const mm = Math.floor(totalSeconds / 60);
    const ss = totalSeconds % 60;
    return `${String(mm).padStart(2, '0')}:${String(ss).padStart(2, '0')}`;
  });

  async function copyToken() {
    if (!code.value) return;
    try {
      await copyTextToClipboard(code.value);
      ui.showToast(AlertType.Success, 'Token copiado al portapapeles');
    } catch (e) {
      ui.showToast(AlertType.Error, 'No se pudo copiar el token');
    }
  }

  const close = () => {
    modals.hide('pos-admin-grant-code');
  };
</script>

<template>
  <div class="modals-pos-admin-grant-code">
    <v-card class="card">
      <v-card-title class="card-title">
        <div class="title-left">
          <v-icon color="primary" class="title-icon">mdi-shield-key</v-icon>
          <span>Token de autorización POS</span>
        </div>
        <v-spacer />
        <v-btn icon small @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-card-title>

      <v-card-text class="card-body">
        <!-- Loading State -->
        <div v-if="isLoading" class="loading-state">
          <v-progress-circular indeterminate color="primary" size="48" />
          <p class="loading-text">Generando token...</p>
        </div>

        <!-- Error State -->
        <div v-else-if="hasError" class="error-state">
          <v-icon color="error" size="48">mdi-alert-circle-outline</v-icon>
          <p class="error-text">
            {{ errorText || 'No se pudo generar el token' }}
          </p>
          <v-btn color="primary" depressed @click="generateToken">
            <v-icon left>mdi-refresh</v-icon>
            Reintentar
          </v-btn>
        </div>

        <!-- Success State -->
        <template v-else-if="code">
          <div class="code-section">
            <div class="code-box">
              <code class="code-text">{{ code }}</code>
            </div>

            <div class="code-actions">
              <v-btn
                color="primary"
                depressed
                @click="copyToken"
                class="copy-btn"
              >
                <v-icon left>mdi-content-copy</v-icon>
                Copiar
              </v-btn>

              <div class="timer">
                <v-icon
                  class="timer-icon"
                  :color="isExpired ? 'error' : 'grey'"
                >
                  mdi-timer
                </v-icon>
                <span class="timer-text">
                  {{ isExpired ? 'Expirado' : formattedRemaining }}
                </span>
              </div>
            </div>

            <v-progress-linear
              class="progress"
              :value="progress"
              height="10"
              rounded
              :color="progressColor"
              :background-opacity="0.15"
            />
          </div>

          <v-alert type="info" dense outlined class="note">
            <div class="note-text">
              <strong>Token de un solo uso:</strong> válido por 5 minutos. Al
              generar uno nuevo, el anterior deja de funcionar.
            </div>
          </v-alert>
        </template>
      </v-card-text>

      <v-card-actions class="card-actions">
        <v-spacer />
        <v-btn color="primary" @click="close">Cerrar</v-btn>
      </v-card-actions>
    </v-card>
  </div>
</template>

<style scoped lang="scss">
  .modals-pos-admin-grant-code {
    .card {
      @apply tw-rounded-xl;
    }

    .card-title {
      @apply tw-flex tw-items-center;

      .title-left {
        @apply tw-flex tw-items-center tw-gap-2;

        .title-icon {
          @apply tw-opacity-90;
        }
      }
    }

    .card-body {
      @apply tw-space-y-4;
    }

    .loading-state,
    .error-state {
      @apply tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-3;
      @apply tw-py-8;

      .loading-text,
      .error-text {
        @apply tw-text-sm tw-text-gray-600;
      }
    }

    .code-section {
      @apply tw-space-y-3;

      .code-box {
        @apply tw-bg-gray-50 tw-border-2 tw-border-dashed tw-border-gray-200 tw-rounded-lg;
        @apply tw-py-4 tw-text-center;

        .code-text {
          @apply tw-text-4xl tw-font-mono tw-font-bold tw-tracking-widest tw-text-gray-900;
          @apply tw-select-all;
        }
      }

      .code-actions {
        @apply tw-flex tw-items-center tw-justify-between tw-gap-3;
        @apply tw-flex-wrap;

        .copy-btn {
          @apply tw-font-medium;
        }

        .timer {
          @apply tw-flex tw-items-center tw-gap-2;

          .timer-icon {
            @apply tw-text-base;
          }

          .timer-text {
            @apply tw-font-mono tw-text-sm tw-text-gray-700;
          }
        }
      }

      .progress {
        @apply tw-mt-1;
      }
    }

    .note {
      .note-text {
        @apply tw-text-xs tw-leading-relaxed tw-text-gray-700;
      }
    }

    .card-actions {
      @apply tw-px-4 tw-pb-4;
    }
  }
</style>
